import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { jsonLdScript } from "@/lib/jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { ExternalLink, FileText, CheckCircle2 } from "lucide-react";

const TITLE = "আয়কর রিটার্ন দাখিলের জন্য প্রয়োজনীয় কাগজপত্র | Tax Return Documents BD";
const DESC = "e-Return দাখিলের আগে কি কি কাগজপত্র লাগবে জেনে নিন - NID, TIN, বেতন বিবরণী থেকে শুরু করে বিনিয়োগের প্রমাণ পর্যন্ত সম্পূর্ণ তালিকা।";
const URL = "https://onlinetaxvat.com/tax-return-documents-bd";

const DOCS = [
  { t: "জাতীয় পরিচয়পত্র (NID)", d: "করদাতার পরিচয় যাচাইয়ের জন্য বাধ্যতামূলক।" },
  { t: "TIN Certificate", d: "e-Return সিস্টেমে লগইন ও রিটার্ন দাখিলের জন্য প্রয়োজন।" },
  { t: "বেতনের বিবরণী / Salary Certificate", d: "চাকরিজীবীদের জন্য নিয়োগকর্তার দেওয়া বার্ষিক বেতন বিবরণী।" },
  { t: "ব্যাংক স্টেটমেন্ট", d: "সুদ/মুনাফা এবং লেনদেনের তথ্যের জন্য।" },
  { t: "সঞ্চয়পত্রের সার্টিফিকেট", d: "সঞ্চয়পত্র থাকলে সার্টিফিকেট ও উৎসে কর কর্তনের প্রমাণ।" },
  { t: "অন্য উৎসের আয়ের প্রমাণ", d: "বাড়ি ভাড়া, ব্যবসা বা অন্য উৎস থেকে আয় থাকলে সংশ্লিষ্ট ডকুমেন্ট।" },
  { t: "বিনিয়োগের প্রমাণ", d: "জীবন বিমা, DPS, স্টক ইত্যাদি — কর রেয়াত পাওয়ার জন্য।" },
  { t: "সম্পদ ও দায়ের তথ্য", d: "গাড়ি, জমি, ঋণ ইত্যাদির বিবরণ।" },
];

export const Route = createFileRoute("/tax-return-documents-bd")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      webPageScript({ path: "/tax-return-documents-bd", name: TITLE, description: DESC }),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "আয়কর রিটার্ন দাখিলের জন্য প্রয়োজনীয় কাগজপত্র",
        description: DESC,
        inLanguage: "bn",
        mainEntityOfPage: URL,
        author: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
        publisher: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
      }),
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <article className="container-page max-w-3xl py-14 md:py-20">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            <FileText className="h-4 w-4" /> Tax Return Documents BD
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            আয়কর রিটার্ন দাখিলের জন্য প্রয়োজনীয় কাগজপত্র
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            e-Return দাখিলের আগে হাতের কাছে যেসব তথ্য ও কাগজপত্র রাখা দরকার তার সম্পূর্ণ তালিকা।
          </p>
        </header>

        <section className="glass rounded-3xl border border-border/60 p-6 md:p-8 shadow-card">
          <h2 className="font-display text-xl md:text-2xl font-bold mb-6">প্রয়োজনীয় ডকুমেন্টের তালিকা</h2>
          <ul className="space-y-4">
            {DOCS.map((d, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <div>
                  <h3 className="font-semibold text-base">{d.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-5 md:p-6">
          <p className="text-sm md:text-base leading-relaxed">
            <strong>মনে রাখবেন:</strong> e-Return সিস্টেমে ডকুমেন্ট আপলোড করার প্রয়োজন নেই — শুধু সঠিক তথ্য ইনপুট দিলেই হবে। তবে অডিট বা যাচাইয়ের সময় প্রয়োজন হতে পারে, তাই কাগজপত্র সংরক্ষণ করুন।
          </p>
        </section>

        <section className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl border border-border/60 bg-card">
          <div>
            <h3 className="font-display font-bold">আরও জানুন</h3>
            <Link to="/knowledge-hub/$slug" params={{ slug: "faq-e-return" }} className="text-primary hover:underline text-sm">
              → e-Return ফাইলিং সম্পর্কিত ৪৫টি সচরাচর জিজ্ঞাসা দেখুন
            </Link>
          </div>
          <a
            href="https://etaxnbr.gov.bd"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition shadow-md"
          >
            etaxnbr.gov.bd এ যান <ExternalLink className="h-4 w-4" />
          </a>
        </section>
        <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["tax-return-documents-bd"]} />
      </article>
    </SiteShell>
  );
}
